import { geoJsonCoordinates } from '../ResourceTab/resourceSetToRow';
import {
  assetSymbolTargetFor,
  type AssetSymbolSource,
} from '../EventsTab/assetSymbolTargets';

export const ORG_ASSETS_LAYER_ID = 'org-assets';
export const ORG_ASSETS_LAYER_TITLE = 'Org assets';
export const DEFAULT_ORG_ASSET_SYMBOL = 'facility';

export type OrgAssetPinSource = AssetSymbolSource & {
  archived?: boolean | null;
  symbology?: string | null;
  geometry?: unknown;
  geolocation?: unknown;
};

export interface OrgAssetPin {
  resource_set_id: string;
  name: string;
  symbology: string;
  longitude: number;
  latitude: number;
}

/** Point for an asset, from its GeoJSON geometry or stored geolocation. */
export function orgAssetPoint(
  resource: Pick<OrgAssetPinSource, 'geometry' | 'geolocation'>
): [number, number] | undefined {
  return (
    geoJsonCoordinates(resource.geometry) ??
    geoJsonCoordinates(resource.geolocation)
  );
}

/**
 * True when the asset has a symbol the map can show: a point or a linked ArcGIS
 * feature.
 */
export function assetHasMapSymbol(resource: OrgAssetPinSource): boolean {
  return (
    !!orgAssetPoint(resource) ||
    assetSymbolTargetFor(resource).objectId !== undefined
  );
}

/**
 * Assets the org-assets layer should draw: located, not archived, and not
 * already on the map as a linked ArcGIS feature or another layer's graphic
 * (`drawnKeys` holds the ids and names of those graphics).
 */
export function selectOrgAssetPins(
  resources: OrgAssetPinSource[],
  drawnKeys: ReadonlySet<string>
): OrgAssetPin[] {
  const pins: OrgAssetPin[] = [];
  for (const resource of resources) {
    if (resource.archived) continue;
    const point = orgAssetPoint(resource);
    if (!point) continue;
    const target = assetSymbolTargetFor(resource);
    if (target.objectId !== undefined) continue;
    if (target.matchIds.some((id) => drawnKeys.has(id))) continue;
    pins.push({
      resource_set_id: resource.resource_set_id,
      name: resource.name ?? '',
      symbology: resource.symbology || DEFAULT_ORG_ASSET_SYMBOL,
      longitude: point[0],
      latitude: point[1],
    });
  }
  return pins;
}
